import { useId, useMemo } from "react";
import { TableColumn } from "@components/Table/TableColumns";
import { getRem, markMatch, Tooltip, usePlatformFeatureFlag } from "@datassential/platform-ui-lib";
import { ArtificialMetric, useVisualizeMetric, VisualizeDataItem, VisualizeMetric } from "@api/useVisualizeData";
import { AiOutlineInfoCircle } from "react-icons/ai";
import Popup from "@components/Popup";
import { FaCheck } from "react-icons/fa";
import HeatScoreLabel from "@vms/HeatScoreLabel";
import { HeatScore } from "@models/HeatScore";
import { useVisualizeContext, VISUALIZE_VIEWS, VisualizeView } from "@pages/Visualize/VisualizeContext";
import { containsAll } from "@core/utils/array";
import { Metric } from "@models/Metric";
import TableCellSelectKeyword from "@vms/TableCells/TableCellSelect";

export const NUM_COL_WIDTH = 100;
export const FAMILIAR_FAVORITES_COL_WIDTH = 130;
export const HEAT_SCORE_COL_WIDTH = 120;

type TableMetric = {
    id: VisualizeMetric;
    index: number;
    name: string;
    sortingKey: string;
    hasNorm?: boolean;
}

export type VisualizeTableConfig = {
    data: VisualizeDataItem[];
    search: string;
}


export function useVisualizeTableConfig({
    data,
    search,
}: VisualizeTableConfig) {
    const {
        xMetricId,
        yMetricId,
        activeView,
    } = useVisualizeContext();

    const polarityTooltipId = useId();
    const trimmedSearch = useMemo(() => search.trim(), [search])
    const hideSorting = useMemo(() => trimmedSearch.length > 0, [trimmedSearch])
    const isOriginalRawValuesEnabled = usePlatformFeatureFlag('CNPF original raw values');

    const xMetric = useVisualizeMetric(xMetricId);
    const yMetric = useVisualizeMetric(yMetricId);

    const metrics = useMemo(
        () => {
            let metrics: TableMetric[] = [
                {
                    id: xMetric?.id ?? Metric.NoData,
                    index: 0,
                    name: xMetric?.name ?? '',
                    sortingKey: 'xMetric',
                },
                {
                    id: yMetric?.id ?? Metric.NoData,
                    index: 1,
                    name: yMetric?.name ?? '',
                    sortingKey: 'yMetric',
                },
            ]
                .map(i => ({
                    ...i,
                    name: i.id === ArtificialMetric.KnowItChange ? 'Know It 2 Yr Change' : i.name,
                }));


            if (metrics[0].id === ArtificialMetric.KnowItChange) {
                metrics = [metrics[1], metrics[0]];
            }

            return metrics;
        },
        [xMetric, yMetric]
    );

    const isChangeMetricActive = useMemo(
        () => containsAll([xMetricId, yMetricId], [ArtificialMetric.KnowItChange]),
        [xMetricId, yMetricId]
    );

    return useMemo(
        (): TableColumn[] => [
            {
                key: 'name',
                sortKey: hideSorting ? undefined : 'name',
                head: (
                    <div className="VisualizeTable__item-head">
                        <span className="VisualizeTable__item-title">Keyword name</span>
                    </div>
                ),
                headText: 'Keyword Name',
                accentLevel: 1,
                Value: ({ index }) => {
                    const keywordId = data[index].id ?? 0
                    return (
                        <TableCellSelectKeyword
                            keywordId={keywordId}
                            className="VisualizeTable__item-name"
                        >
                            <span
                                className="VisualizeTable__toggle-label"
                                data-testid="textCorrelationItem"
                                dangerouslySetInnerHTML={{
                                    __html: markMatch(data[index]?.name ?? '', trimmedSearch, true)
                                }}
                            />
                        </TableCellSelectKeyword>
                    )
                },
                valueText: (index) => data[index]?.name ?? '',
                style: {
                    cell: {
                        paddingLeft: getRem(16),
                    }
                }
            },
            {
                key: 'macStage',
                head: '',
                headText: 'MAC Stage',
                isHiddenForRender: true,
                Value: () => null,
                valueText: (index) => `${data[index].macStageTitle}`,
            },
            {
                key: 'baseSize',
                head: '',
                headText: 'Base Size',
                isHiddenForRender: true,
                Value: () => null,
                valueText: (index) => `${data[index].base}`,
            },
            ...metrics.map((metric) => ({
                key: metric.name,
                sortKey: hideSorting ? undefined : metric.sortingKey,
                isHiddenForTextMode: true,
                head: (
                    <div className="VisualizeTable__item-head">
                        <span className="VisualizeTable__item-title">{metric.name}</span>
                    </div>
                ),
                Value: ({ index }) => {
                    return (
                        <div
                            className="VisualizeTable__item-value"
                        >
                            {data[index].metrics[metric.index].tableValue}
                        </div>
                    )
                },
                accentLevel: 1,
                style: {
                    cell: {
                        flex: `0 0 ${getRem(NUM_COL_WIDTH)}`,
                        justifyContent: "center",
                        textAlign: "center",
                    }
                }
            }) as TableColumn),
            {
                head: null,
                headText: "RAW",
                key: 'raw',
                Value: () => null,
                isHiddenForRender: true,
                subColumns: metrics.map((metric) =>
                    ({
                        key: metric.name,
                        head: null,
                        headText: `${metric.name}`,
                        Value: () => null,
                        valueText: (index) => data[index].metrics[metric.index].copyTableValue,
                    }) as TableColumn)
            },
            {
                head: null,
                headText: "ORIGINAL RAW (UNROUNDED)",
                key: 'original-raw',
                Value: () => null,
                isHiddenForRender: true,
                isHidden: !isOriginalRawValuesEnabled,
                subColumns: metrics.map((metric) =>
                    ({
                        key: metric.name,
                        head: null,
                        headText: `${metric.name}`,
                        Value: () => null,
                        valueText: (index) => `${data[index].metrics[metric.index].sortValue}`,
                    }) as TableColumn)
            },
            {
                head: null,
                headText: "NORMS VERSUS: ALL FOOD & BEV",
                key: 'norms',
                Value: () => null,
                isHiddenForRender: true,
                subColumns: metrics
                    // .filter((metric) => metric.hasNorm)
                    .flatMap((metric) => [
                        {
                            key: `${metric.name}Norm`,
                            head: null,
                            headText: `${metric.name} Percentile`,
                            Value: () => null,
                            valueText: (index) => `${data[index].metrics[metric.index].norm}`,
                            isHiddenForRender: true,
                        },
                    ] as TableColumn[]),
            },
            {
                key: 'polarity',
                sortKey: hideSorting ? undefined : 'polarityRaw',
                head: <span style={{ position: 'relative' }}>
                    POLARITY
                    <AiOutlineInfoCircle
                        className="VisualizeTable__info-icon"
                        data-tooltip-id={polarityTooltipId}
                    />
                    <Popup>
                        <Tooltip
                            id={polarityTooltipId}
                            place="bottom-end"
                        >
                            Measures the relationship between
                            <br/>the top and bottom of the affinity scale;
                            <br/>100-point scale, higher scores indicate
                            <br/>more polarizing foods and flavors.
                        </Tooltip>
                    </Popup>
                </span>,
                headText: 'POLARITY',
                isHidden: activeView !== VisualizeView.Polarity,
                accentLevel: 1,
                Value: ({ index }) => (<>
                    {data[index].polarityScore}
                </>),
                valueText: (index) => `${data[index].polarityScore}`,
                style: {
                    cell: {
                        flex: `0 0 ${getRem(NUM_COL_WIDTH)}`,
                        justifyContent: "center",
                        paddingRight: getRem(20),
                    }
                },
            },
            {
                key: 'heat-score',
                sortKey: hideSorting ? undefined : 'heatScoreNum',
                head: "Heat Score",
                headText: 'HEAT SCORE',
                isHidden: !isChangeMetricActive,
                accentLevel: 1,
                Value: ({ index }) => data[index]?.heatScore ? (
                    <HeatScoreLabel
                        heatScoreId={data[index]?.heatScore ?? HeatScore.NoData}
                        modifiers={["medium"]}
                    />
                ) : <>-</>,
                valueText: (index) => data[index]?.heatScoreTitle ?? '',
                style: {
                    cell: {
                        flex: `0 0 ${getRem(HEAT_SCORE_COL_WIDTH)}`,
                        justifyContent: "center",
                    }
                },
            },
            {
                key: 'familiar-favorites',
                sortKey: hideSorting ? undefined : 'familiarFavoritesNum',
                head: VISUALIZE_VIEWS.get(VisualizeView.FamiliarFavorites)?.name,
                headText: 'FAMILIAR FAVORITES',
                isHidden: activeView !== VisualizeView.FamiliarFavorites,
                accentLevel: 1,
                Value: ({ index }) => (
                    data[index].isFamiliarFavorites ? <FaCheck/> : null
                ),
                valueText: (index) => `${data[index].isFamiliarFavorites ? "Yes" : "No"}`,
                style: {
                    cell: {
                        flex: `0 0 ${getRem(FAMILIAR_FAVORITES_COL_WIDTH)}`,
                        justifyContent: "center",
                    }
                },
            },
            {
                key: 'rising-stars',
                sortKey: hideSorting ? undefined : 'risingStarsNum',
                head: VISUALIZE_VIEWS.get(VisualizeView.RisingStars)?.name,
                headText: 'RISING STARS',
                isHidden: activeView !== VisualizeView.RisingStars,
                accentLevel: 1,
                Value: ({ index }) => (
                    data[index].isRisingStars ? <FaCheck/> : null
                ),
                valueText: (index) => `${data[index].isRisingStars ? "Yes" : "No"}`,
                style: {
                    cell: {
                        flex: `0 0 ${getRem(NUM_COL_WIDTH)}`,
                        justifyContent: "center",
                    }
                },
            },
        ],
        [
            data,
            trimmedSearch,
            metrics,
            hideSorting,
            polarityTooltipId,
            activeView,
            isChangeMetricActive,
            isOriginalRawValuesEnabled,
        ]
    );
}